import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAiChat } from 'modules/ai/chat/useAiChat'

import i18n from 'modules/core/lib/i18n'

import { useOne, useOneFeatureAvailable } from 'modules/ai/lib/useOne'
import { OneFooter } from 'modules/planning/components/One/footer'

export const useHeadcountPlanningOne = ({
  canCreatePeriod,
  onStartFromScratch,
}: {
  canCreatePeriod: boolean
  onStartFromScratch: () => void
}) => {
  const oneAvailable = useOneFeatureAvailable()
  const { open: isChatOpen, clear: clearChat } = useAiChat()
  const [fullscreen, setFullscreen] = useState(false)
  const [autoOpenChat, setChatOpen] = useState(false)
  const shouldClearOnNextOpen = useRef(false)

  useEffect(() => {
    if (!isChatOpen) {
      setChatOpen(false)
    }
  }, [isChatOpen])

  useEffect(() => {
    if (isChatOpen && shouldClearOnNextOpen.current) {
      shouldClearOnNextOpen.current = false
      clearChat()
    }
  }, [isChatOpen, clearChat])

  const oneOptions = useMemo(
    () => ({
      initialMessage: i18n.t('planning.one.chat_initial_message_new'),
      autoOpenChat,
      fullscreen,
      footer:
        canCreatePeriod && fullscreen ? (
          <OneFooter onClickStartFromScratch={onStartFromScratch} />
        ) : undefined,
      footerKey: canCreatePeriod && fullscreen,
    }),
    [autoOpenChat, fullscreen, canCreatePeriod, onStartFromScratch]
  )
  useOne<null>(['headcountPlanning'], null, oneOptions)

  const openOneChat = useCallback(() => {
    shouldClearOnNextOpen.current = true
    setFullscreen(true)
    setChatOpen(true)
  }, [])

  const handleCreatePeriod = useCallback(() => {
    if (oneAvailable) {
      openOneChat()
      return
    }

    onStartFromScratch()
  }, [oneAvailable, openOneChat, onStartFromScratch])

  return {
    handleCreatePeriod: canCreatePeriod ? handleCreatePeriod : undefined,
  }
}
